import { useRef, useEffect } from "react";
import { Search, MapPin, Store, Tag, ArrowRight, Sparkles, X, Loader2 } from "lucide-react";
import { useSmartSearch, getUserTown, setUserTown } from "../hooks/useSmartSearch";

export { getUserTown, setUserTown };

const SmartSearch = ({ variant = "default", className = "" }) => {
  const {
    query, setQuery, clearSearch,
    suggestions, bizMatches, aiMessage,
    loading, isOpen, setIsOpen, selectedIndex,
    userTown, handleFullSearch, handleSuggestionClick,
    handleBizClick, handleKeyDown,
  } = useSmartSearch();

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const isDark = variant === "hero";

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) && !inputRef.current?.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setIsOpen]);

  return (
    <div className={`relative ${className}`}>
      {/* Search Input */}
      <div className={`flex items-center rounded-xl overflow-hidden border-2 transition-colors ${
        isDark
          ? "bg-white/10 border-white/20 focus-within:border-[#D4A017]/60"
          : "bg-white border-[#D1CFC5] focus-within:border-[#2C4A3B]"
      }`}>
        <div className="pl-4">
          <Search className={`w-5 h-5 ${isDark ? "text-white/50" : "text-[#5C5A52]"}`} />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.length >= 2 && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={userTown ? `Search businesses near ${userTown}...` : "What are you looking for?"}
          className={`flex-1 px-3 py-3.5 bg-transparent outline-none text-base ${
            isDark ? "text-white placeholder:text-white/40" : "text-[#1A1915] placeholder:text-[#5C5A52]"
          }`}
          data-testid="smart-search-input"
        />
        {query && (
          <button onClick={clearSearch} className={`px-2 ${isDark ? "text-white/50 hover:text-white" : "text-[#5C5A52] hover:text-[#1A1915]"}`}>
            <X className="w-4 h-4" />
          </button>
        )}
        <button
          onClick={handleFullSearch}
          className="bg-[#2C4A3B] hover:bg-[#1A2F23] text-white px-5 py-3.5 font-semibold transition-colors"
          data-testid="smart-search-btn"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Search"}
        </button>
      </div>

      {/* AI hint */}
      {aiMessage && isOpen && (
        <div className={`mt-1.5 flex items-center gap-1.5 text-xs ${isDark ? "text-[#D4A017]" : "text-[#2C4A3B]"}`}>
          <Sparkles className="w-3 h-3" />
          <span>{aiMessage}</span>
        </div>
      )}

      {/* Dropdown */}
      <SearchDropdown
        dropdownRef={dropdownRef}
        isOpen={isOpen}
        suggestions={suggestions}
        bizMatches={bizMatches}
        selectedIndex={selectedIndex}
        query={query}
        onSuggestionClick={handleSuggestionClick}
        onBizClick={handleBizClick}
        onFullSearch={handleFullSearch}
      />
    </div>
  );
};

const SearchDropdown = ({ dropdownRef, isOpen, suggestions, bizMatches, selectedIndex, query, onSuggestionClick, onBizClick, onFullSearch }) => {
  if (!isOpen || (suggestions.length === 0 && bizMatches.length === 0)) return null;

  return (
    <div ref={dropdownRef} className="absolute z-50 mt-2 w-full bg-white border-2 border-[#D1CFC5] rounded-xl shadow-xl overflow-hidden max-h-[360px] overflow-y-auto" data-testid="search-dropdown">
      {suggestions.length > 0 && (
        <div className="p-2">
          <p className="text-[10px] text-[#5C5A52] uppercase tracking-wider px-2 pb-1">Suggestions</p>
          {suggestions.map((s, i) => (
            <button key={`${s.type}-${s.value}`} onClick={() => onSuggestionClick(s)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${selectedIndex === i ? "bg-[#2C4A3B]/10" : "hover:bg-[#F7F6F2]"}`}>
              {s.type === "category" ? <Tag className="w-4 h-4 text-[#D4A017] flex-shrink-0" /> : <MapPin className="w-4 h-4 text-[#2C4A3B] flex-shrink-0" />}
              <span className="text-sm text-[#1A1915]">{s.label}</span>
              <ArrowRight className="w-3 h-3 text-[#5C5A52] ml-auto" />
            </button>
          ))}
        </div>
      )}
      {bizMatches.length > 0 && (
        <div className="border-t border-[#D1CFC5] p-2">
          <p className="text-[10px] text-[#5C5A52] uppercase tracking-wider px-2 pb-1">Businesses</p>
          {bizMatches.map((b, i) => (
            <button key={b.id} onClick={() => onBizClick(b)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${selectedIndex === suggestions.length + i ? "bg-[#2C4A3B]/10" : "hover:bg-[#F7F6F2]"}`}>
              <Store className="w-4 h-4 text-[#5C5A52] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#1A1915] truncate">{b.name}</p>
                <p className="text-[11px] text-[#5C5A52]">{b.category} · {b.town}</p>
              </div>
              {b.membership_tier === "founding" && (
                <span className="text-[9px] bg-[#14B8A6]/15 text-[#14B8A6] px-1.5 py-0.5 rounded-full font-semibold">Founding</span>
              )}
            </button>
          ))}
        </div>
      )}
      <div className="border-t border-[#D1CFC5]">
        <button onClick={onFullSearch} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#F7F6F2] transition-colors">
          <Search className="w-4 h-4 text-[#2C4A3B]" />
          <span className="text-sm text-[#2C4A3B] font-medium">Search all for &quot;{query}&quot;</span>
          <ArrowRight className="w-3 h-3 text-[#2C4A3B] ml-auto" />
        </button>
      </div>
    </div>
  );
};

export default SmartSearch;
